import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isMobile } from '../../../utils/platformResolver';
import { isDiiaUser } from '../../../utils/billingInfoParser';
import { sendDiiaDeepLinkRequest } from '../../../utils/api/accountApi';
import { genericErrorMessage } from '../../../utils/lang/commonErrors';
import { logDiiaQRShown } from '../../../utils/analytics/diiaEvents';
import Loader from '../../common/Loader';
import { type UserProfile } from '../../../utils/api/schema/profileSchema';
import { getParsedParams } from '../../../utils/consts/params';
import DiiaModal from './components/DiiaModal';
import diiaButtonImage from '../../../../assets/icons/diia/diia-button.svg';
import styles from './DiiaBlock.scss';

interface DiiaBlockProps {
  profile: UserProfile;
}

const successRedirectDelayMills = 300;

const DiiaBlock = ({ profile }: DiiaBlockProps) => {
  const intl = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const [successRedirectURL, setSuccessRedirectURL] = useState<string | undefined>();

  useEffect(() => {
    const params = getParsedParams();

    const redirectUrl = params.DIIA_SUCCESS_URL;

    if (redirectUrl) {
      setSuccessRedirectURL(redirectUrl);
      setIsModalOpen(true);
    }
  }, [profile]);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const onDiiaButtonClick = () => {
    if (isMobile()) {
      setIsLoading(true);
      sendDiiaDeepLinkRequest()
        .then((res) => {
          setIsLoading(false);
          logDiiaQRShown();
          setTimeout(() => {
            // letting analytics event sink in
            location.href = res.deep_link;
          }, successRedirectDelayMills);
        })
        .catch((error_: unknown) => {
          console.error('Could not fetch Diia deeplink:', error_);
          setError(intl.formatMessage(genericErrorMessage));
        });
    } else {
      setIsModalOpen(true);
    }
  };

  if (
    isDiiaUser(profile) ||
    profile.email.endsWith('@anonymous.device') ||
    profile.subscription?.hasActiveSubscription
  ) {
    return null;
  }

  return (
    <section className={styles.section}>
      <div className={styles.block}>
        <div>
          <h4 className={styles.blockTitle}>
            <FormattedMessage
              id="profile.diia.title"
              defaultMessage="Ukraine stands together!"
              description="Title of the Diia block"
            />
            <span role="img" aria-label="Ukrainian flag emoji">
              🇺🇦
            </span>
          </h4>
          <span className={styles.blockSubtitle}>
            <FormattedMessage
              id="profile.diia.subtitle"
              defaultMessage="Verify your Ukrainian citizenship to unlock all ClearVPN features for free!"
              description="Subtitle of the Diia block"
            />
          </span>
        </div>

        <button type="button" disabled={isLoading} onClick={onDiiaButtonClick}>
          {isLoading ? (
            <Loader />
          ) : (
            <img
              src={diiaButtonImage}
              width="48"
              height="48"
              alt="Click to authenticate with Diia — national e-signature service of Ukraine"
            />
          )}
        </button>

        {error ? <span className={styles.error}>{error}</span> : null}
      </div>

      <DiiaModal isOpen={isModalOpen} successRedirectURL={successRedirectURL} onClose={onModalClose} />
    </section>
  );
};

export default DiiaBlock;
